import React, { useCallback, useEffect } from "react";
import FavouriteListItem from "./FavouriteListItem/FavouriteListItem";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import { useNavigate } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { Button, LoadingSpinner } from "pepsico-ds";
import {
  fetchFavouriteSessions,
  setFavouriteSession,
  unFavouriteAll,
} from "../../../../redux/reducers/chatFavourites/chatFavoriteReducer";
import { CloseOutRadioActions } from "../../../../redux/closeoutslice/closeOutRadioSlice";
import {
  setActiveChat,
  setActiveQuestion,
  setIsArchived,
  setShowWelcomeText,
} from "../../../../redux/actions/chatActions";
import { setActiveTab } from "../../../../redux/reducers/chatHistoryActionReducer";

function FavouriteList() {
  const limit = 15;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [page, setPage] = React.useState(1);
  const dispatch = useAppDispatch();
  const {
    isFavouriteloading: isLoading,
    isEndOfFavouriteData: hasFetchedAll,
    favouriteSessions: data,
    isFavouriteError: isError,
    isFetchingMore,
  } = useAppSelector((state) => state.favouriteChat);

  const total = data?.length || 0;
  const navigate = useNavigate();

  const goToDefaultView = useCallback(() => {
    dispatch({ type: "CLEAR_MESSAGES" });
    dispatch(setActiveQuestion("", ""));
    dispatch(setShowWelcomeText(true));
    dispatch(setIsArchived(false));
    dispatch(setFavouriteSession({}));
    dispatch(setActiveChat(""));
    navigate(`/spend-wise-hub/chat-assistant`, {
      replace: true,
    });
  }, [dispatch, navigate]);

  useEffect(() => {
    //todo: fetch archive chats from API
    dispatch(fetchFavouriteSessions({ page: 1, limit }))
      .then(unwrapResult)
      .then((result) => {
        const response = result.data;
        if (Array.isArray(response) && response.length > 0) {
          navigate(`/spend-wise-hub/chat-assistant/${response[0].chat_id}`, {
            replace: true,
          });
        } else {
          goToDefaultView();
        }
      });
  }, [dispatch]);

  const unFavouriteAllHandler = () => {
    dispatch(unFavouriteAll())
      .then(unwrapResult)
      .then(() => {
        dispatch(
          CloseOutRadioActions.setSnackBarMessage({
            message: "All Chats removed from favorites successfully.",
            severity: "success",
          })
        );
        dispatch(setActiveTab("chatHistory"));
        goToDefaultView();
      })
      .catch((error) => {
        dispatch(
          CloseOutRadioActions.setSnackBarMessage({
            message: "Failed to remove all chats from favorites.",
            severity: "error",
          })
        );
      });
  };
  // when you scroll the containerRef, it should load more chats
  const handleScroll = () => {
    if (
      containerRef.current &&
      containerRef.current.scrollHeight - containerRef.current.scrollTop <=
        containerRef.current.clientHeight + 50
    ) {
      if (isFetchingMore) return; // Prevent multiple fetches
      if (hasFetchedAll) return; // If all data has been fetched, do not fetch more
      setPage((prevPage) => prevPage + 1);
      dispatch(fetchFavouriteSessions({ page: page + 1, limit }));
    }
  };
  return (
    <div style={{ flex: 1, position: "relative" }}>
      {isLoading ? (
        <LoadingSpinner
          style={{ color: "#005cbc", margin: "0 auto" }}
          size={{
            height: "20px",
            width: "20px",
          }}
        />
      ) : isError ? (
        <p style={{ textAlign: "center" }}>Error loading archived chats</p>
      ) : total === 0 ? (
        <p style={{ textAlign: "center" }}>No favorite chats found</p>
      ) : (
        <>
          <div
            style={{ height: "55vh", overflowY: "auto" }}
            ref={containerRef}
            onScroll={handleScroll}
          >
            {data?.map((chat) => (
              <FavouriteListItem
                key={chat.chat_id}
                sessionName={chat.session_name}
                chatId={chat.chat_id}
                updatedDate={chat.updated_date}
              />
            ))}
          </div>
          {isFetchingMore && (
            <LoadingSpinner
              style={{ color: "#005cbc", margin: "0 auto" }}
              size={{
                height: "20px",
                width: "20px",
              }}
            />
          )}
          {total ? (
            <Button
              variant="tertiary"
              style={{
                width: "100%",
                justifyContent: "center",
                position: "absolute",
                bottom: 0,
                zIndex: 100,
              }}
              iconLeading="star_border"
              onClick={unFavouriteAllHandler}
            >
              Unfavorite All
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}

export default FavouriteList;
